import React from 'react';
import { useTranslation } from 'react-i18next';
import { EventCategory, EventStatus } from '../../models/event/event';
import { cn } from '../../utils/class-names-util';

interface EventsFilterBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filters: {
    category?: EventCategory;
    status?: EventStatus;
  };
  onFiltersChange: (filters: { category?: EventCategory; status?: EventStatus }) => void;
  dateRange: {
    start: string;
    end: string;
  };
  onDateRangeChange: (range: { start: string; end: string }) => void;
  onClearFilters: () => void;
  totalResults: number;
  className?: string;
}

const EventsFilterBar: React.FC<EventsFilterBarProps> = ({
  searchQuery,
  onSearchChange,
  filters,
  onFiltersChange,
  dateRange,
  onDateRangeChange,
  onClearFilters,
  totalResults,
  className
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const hasActiveFilters = !!(
    searchQuery ||
    filters.category ||
    filters.status ||
    dateRange.start ||
    dateRange.end
  );

  return (
    <div className={cn("sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm", className)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Search and Results */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <input
                type="text"
                placeholder={t('search.placeholder')}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className={cn(
                  "w-full py-3 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white",
                  isRTL ? "pr-12 pl-4" : "pl-12 pr-4"
                )}
              />
              <svg
                className={cn(
                  "absolute top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400",
                  isRTL ? "right-4" : "left-4"
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600 font-medium">
              {t('search.results.showing', { count: totalResults })}
            </span>
            {hasActiveFilters && (
              <button
                onClick={onClearFilters}
                className="text-sm text-red-600 hover:text-red-800 font-medium transition-colors"
              >
                {t('filter.clear')}
              </button>
            )}
          </div>
        </div>

        {/* Filter Pills */}
        <div className="space-y-3 sm:space-y-0 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
          {/* Category Filter */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              {t('filter.category.title')}:
            </label>
            <select
              value={filters.category || ''}
              onChange={(e) => onFiltersChange({
                ...filters,
                category: e.target.value ? e.target.value as EventCategory : undefined
              })}
              className="w-full sm:w-auto text-sm border border-gray-300 rounded-md px-3 py-1.5 pr-8 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
            >
              <option value="">{t('filter.category.all')}</option>
              {Object.values(EventCategory).map(category => (
                <option key={category} value={category}>
                  {t(`pages.events.categories.${category}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              {t('filter.status.title')}:
            </label>
            <select
              value={filters.status || ''}
              onChange={(e) => onFiltersChange({
                ...filters,
                status: e.target.value ? e.target.value as EventStatus : undefined
              })}
              className="w-full sm:w-auto text-sm border border-gray-300 rounded-md px-3 py-1.5 pr-8 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
            >
              <option value="">{t('filter.status.all')}</option>
              {Object.values(EventStatus).map(status => (
                <option key={status} value={status}>
                  {t(`pages.events.status.${status}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range - Stacked on mobile */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                {t('filter.dateRange.start')}:
              </label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => onDateRangeChange({ ...dateRange, start: e.target.value })}
                className="w-full sm:w-auto text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                {t('filter.dateRange.end')}:
              </label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => onDateRangeChange({ ...dateRange, end: e.target.value })}
                className="w-full sm:w-auto text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
              />
            </div>
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <span className="text-xs font-medium text-gray-500">
              {t('filter.active')}:
            </span>
            {searchQuery && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-black text-white">
                "{searchQuery}"
                <button
                  onClick={() => onSearchChange('')}
                  className="ml-1 hover:bg-gray-800 rounded-full p-0.5"
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </span>
            )}
            {filters.category && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {t(`pages.events.categories.${filters.category}`)}
                <button
                  onClick={() => onFiltersChange({ ...filters, category: undefined })}
                  className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </span>
            )}
            {filters.status && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {t(`pages.events.status.${filters.status}`)}
                <button
                  onClick={() => onFiltersChange({ ...filters, status: undefined })}
                  className="ml-1 hover:bg-green-200 rounded-full p-0.5"
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </span>
            )}
            {(dateRange.start || dateRange.end) && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                {t('filter.dateRange.active')}
                <button
                  onClick={() => onDateRangeChange({ start: '', end: '' })}
                  className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsFilterBar;